<template>
  <div class="detail">
    <div class="detail-title d-flex justify-content-between mb-20 align-items-center">
      <div class="fw-medium">{{ $t('admin.user.basicInformation') }}</div>

      <v-button
        v-if="!userDetails.deletedAt"
        class="btn-edit p-4 text-primary"
        variant="icon"
        @click="editBasicInformation = !editBasicInformation">
        <v-icon icon="pencil"/>
      </v-button>
    </div>

    <div class="detail-content">
      <Transition mode="out-in" name="fade-in">
        <div v-if="!editBasicInformation">
          <div class="text-sm d-flex py-4 align-items-center">
            <span class="info-title fw-medium me-16">{{ $t('admin.user.profileImage') }}: </span>
            <div v-if="profileImageUrl" class="user-profile-image-preview">
              <img :src="profileImageUrl" :alt="userDetails.name" />
            </div>
            <span v-else class="text-muted">{{ $t('admin.user.noProfileImage') }}</span>
          </div>

          <div class="text-sm d-flex py-4">
            <span class="info-title fw-medium">{{ $t('general.shared.name') }}: </span>
            <span>{{ userDetails.name }}</span>
          </div>

          <div class="text-sm d-flex py-4">
            <span class="info-title fw-medium">{{ $t('general.shared.email') }}: </span>
            <span>{{ email }}</span>
          </div>

          <div class="text-sm d-flex py-4">
            <span class="info-title fw-medium me-16">{{ $t('admin.user.roles') || 'Roles' }}: </span>
            <div class="d-flex gap-4 flex-wrap">
              <span v-for="role in userDetails.roles" :key="role.id" class="badge bg-light text-dark fw-normal border" style="font-size: 10px;">
                {{ $t(`general.roles.${role.name}`) }}
              </span>
            </div>
          </div>
        </div>

        <form
          v-else
          id="user-update"
          class="d-flex flex-column justify-content-between h-100"
        >
          <fieldset class="mb-16">
            <legend class="visually-hidden">{{ $t('admin.user.basicInformation') }}</legend>

            <div class="text-sm d-flex py-4">
              <span class="info-title fw-medium">{{ $t('general.shared.name') }}: </span>
              <span>{{ userDetails.name }}</span>
            </div>

            <v-form-input
              id="user-update-email"
              v-model="email"
              :label="`${this.$t('general.shared.email')}*`"
              :type="'email'"
              :yup-errors-variable="errors.email"
              autocomplete="off"
              class="mb-16"
              form-type="outline"
            />

            <div class="mb-16">
              <label class="form-label mb-12 d-block fw-medium">{{ $t('admin.user.roles') || 'Roles' }}</label>
              <div v-for="role in filteredRoles" :key="role.id" class="checkbox checkbox-left mb-8">
                <input
                  :id="`role-edit-${role.id}`"
                  v-model="selectedRoles"
                  :value="role.name"
                  type="checkbox"
                />
                <label :for="`role-edit-${role.id}`" class="checkbox-label text-sm">
                  {{ $t(`general.roles.${role.name}`) }}
                </label>
              </div>
            </div>

            <div class="mb-16">
              <label class="form-label">{{ $t('admin.user.profileImage') }}</label>
              <div v-if="profileImageUrl" class="user-profile-image-preview mb-12">
                <img :src="profileImageUrl" :alt="userDetails.name" />
                <button
                  type="button"
                  class="btn-remove-image"
                  @click="removeProfileImage"
                >
                  <v-icon icon="trash" size="sm" />
                </button>
              </div>
              <v-uploader
                ref="profileImageUploader"
                :initial-value="profileImageMedia"
                :multiple="false"
                :max-items="1"
                route-prefix="api/s3"
                :after-upload="handleProfileImageUpload"
                @removed="handleProfileImageRemove"
              />
            </div>
          </fieldset>

          <v-button
            :disabled="!meta.valid || accepted"
            :is-loading="accepted"
            class="btn-submit-form btn-icon w-fit align-self-end bg-primary border-primary text-white"
            size="sm"
            @click="saveUser"
          >
            {{ $t('general.button.save') }}
            <v-icon icon="arrow-right" size="sm" space="ms-12"/>
          </v-button>
        </form>
      </Transition>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapState } from 'pinia';
import * as yup from 'yup';
import { useField } from 'vee-validate';
import { VFormInput } from '@uniqoders/form';
import { useI18n } from 'vue-i18n';
import useFormValidation from '@/helpers/form';
import VButton from '@/components/vendor/basic/button/VButton.vue';
import api from '@/api';
import useModalUtils from '@/helpers/ModalUtils';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';
import VUploader from '@/components/vendor/upload/VUploader.vue';
import User from '@/api/objects/User';
import useCapabilities from '@/helpers/capabilities';
import { useAuthStore } from '@/stores/auth';
import Role from '@/api/objects/Role';

export default defineComponent({
  name: 'TheAdminUserDetailsBasicInformation',
  components: {
    VIcon,
    VButton,
    VFormInput,
    VUploader,
  },
  emits: [
    'closed',
    'reloadUser',
  ],
  props: {
    userDetails: {
      type: [User, Object],
      required: true,
    },
  },
  setup() {
    const { t } = useI18n();

    const rules = yup.object({
      email: yup.string()
        .email()
        .required()
        .label(t('general.shared.email')),
    });

    const form = useFormValidation(rules);

    const { value: email } = useField('email');

    return {
      ...useCapabilities(),
      ...useModalUtils(),
      ...form,
      email,
    };
  },
  computed: {
    ...mapState(useAuthStore, ['user']),
    filteredRoles() {
      if (this.user?.isSuperAdmin) {
        return this.availableRoles;
      }
      return this.availableRoles.filter((role: any) => role.name !== Role.SUPER_ADMIN);
    },
  },
  data() {
    return {
      editBasicInformation: false as boolean,
      profileImageUrl: '' as string,
      profileImageMedia: [] as any[],
      availableRoles: [] as any[],
      selectedRoles: [] as string[],
    };
  },
  watch: {
    userDetails() {
      this.setData();
    },
  },
  async created() {
    await this.loadRoles();
    this.setData();
  },
  methods: {
    async loadRoles() {
      try {
        this.availableRoles = await api.admin.user.getRoles();
      } catch (e) {
        console.error('Error loading roles:', e);
      }
    },
    setData() {
      if (!!this.userDetails) {
        this.email = this.userDetails.email;
        this.selectedRoles = this.userDetails.roles?.map((role: any) => role.name) || [];
        this.loadProfileImage();
      }
    },
    buildMediaConfirmEntries(mediaItems: any[] = [], collection: string) {
      return (Array.isArray(mediaItems) ? mediaItems : [])
        .map((item: any, index: number) => {
          const attributes = item?.attributes ?? {};
          if (attributes.isExisting) {
            return null;
          }
          const uuid = attributes.uuid ?? item.uuid;
          if (!uuid) {
            return null;
          }
          const order = attributes.order ?? attributes.order_column ?? item.order ?? index + 1;
          return {
            uuid,
            name: attributes.name ?? item.name ?? `media-${order}`,
            order,
            collection,
          };
        })
        .filter((entry: any) => !!entry);
    },
    loadProfileImage() {
      // Get profile image from user media
      const userDetails = this.userDetails as any;
      if (userDetails?.media && Array.isArray(userDetails.media)) {
        console.log(userDetails.media);
        
        const profileMedia = userDetails.media[0];
        console.log(profileMedia);
        
        if (profileMedia) {
          this.profileImageUrl = profileMedia.originalUrl || '';
          this.profileImageMedia = [profileMedia];
        } else {
          this.profileImageUrl = '';
          this.profileImageMedia = [];
        }
      } else {
        this.profileImageUrl = '';
        this.profileImageMedia = [];
      }
    },
    async handleProfileImageUpload(media: any) {
      try {
        // Transform media to the correct format
        const mediaArray = Array.isArray(media) ? media : [media];
        const mediaEntries = this.buildMediaConfirmEntries(mediaArray, 'user_profile');
        
        if (mediaEntries.length === 0) {
          console.error('No valid media entries to confirm');
          return;
        }

        // Get the URL from the uploaded media for preview
        const mediaUrl = mediaArray[0]?.original_url || mediaArray[0]?.preview_url || mediaArray[0]?.url || '';
        if (mediaUrl) {
          this.profileImageUrl = mediaUrl;
        }

        // Confirm the upload to attach it to the user model
        const response = await api.media.confirm('s3', {
          media: mediaEntries,
          model_type: 'user',
          model_id: this.userDetails.id,
          collection: 'user_profile',
        }) as any;

        // Update with the confirmed media URL
        const confirmedMedia = Array.isArray(response) ? response[0] : response;
        if (confirmedMedia) {
          this.profileImageUrl = confirmedMedia.originalUrl || confirmedMedia.original_url || mediaUrl;
        }

        // Reload user to get updated media
        this.$emit('reloadUser');
      } catch (e: any) {
        console.error('Error confirming profile image upload:', e);
        this.$toast.error(e.response?.data?.message || this.$t('general.shared.errorMessage'));
      }
    },
    handleProfileImageRemove() {
      this.profileImageUrl = '';
      this.profileImageMedia = [];
    },
    async removeProfileImage() {
      try {
        const userDetails = this.userDetails as any;
        if (userDetails?.media && Array.isArray(userDetails.media)) {
          const profileMedia = userDetails.media.find(
            (m: any) => m.collection_name === 'user_profile'
          );
          if (profileMedia && profileMedia.id) {
            // Delete the media item
            await api.media.delete('s3', profileMedia.id);
            this.$toast.success(this.$t('general.shared.removed'));
          }
        }
        this.handleProfileImageRemove();
        if (this.$refs.profileImageUploader) {
          (this.$refs.profileImageUploader as any).mediaLibrary.state.media = [];
        }
        // Reload user to get updated media
        this.$emit('reloadUser');
      } catch (e: any) {
        console.error('Error removing profile image:', e);
        this.$toast.error(e.response?.data?.message || this.$t('general.shared.errorMessage'));
      }
    },
    async saveUser() {
      try {
        this.toggleAccepted();

        const data: any = {
          email: this.email,
          roles: this.selectedRoles,
        };

        await api.admin.user.update(this.userDetails.id, data);

        this.$toast.success(this.$t('general.shared.savedChanges'));

        this.$emit('reloadUser');

        this.editBasicInformation = !this.editBasicInformation;
      } catch (e: any) {
        console.error(e);

        this.$toast.error(e.response?.data?.message || this.$t('general.shared.errorMessage'));
      } finally {
        this.toggleAccepted();
      }
    },
  },
});
</script>

<style lang="scss" scoped>
.user-profile-image-preview {
  position: relative;
  display: inline-block;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid var(--uq-border-color, #e0e0e0);
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .btn-remove-image {
    position: absolute;
    top: -4px;
    right: -4px;
    background: rgba(255, 0, 0, 0.9);
    color: white;
    border: none;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
    
    &:hover {
      background: rgba(255, 0, 0, 1);
    }
  }
}

.mb-12 {
  margin-bottom: 0.75rem;
}
</style>
